import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import headerConstants from "../../constants/contants";
import { useMsal } from "@azure/msal-react";
import { Avatar } from "@mui/material";
import styles from "./header.module.css";
import { IconContext } from "react-icons";
import {
	IoIosSearch,
	IoIosHelpCircleOutline,
	IoIosNotificationsOutline,
	IoMdInformationCircleOutline
} from "react-icons/io";
import { FaAngleDown } from "react-icons/fa6";
import { styled, alpha } from '@mui/material/styles';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import ListItemIcon from '@mui/material/ListItemIcon';
import Logout from '@mui/icons-material/Logout';
import User from "@mui/icons-material/Person";
import { useRefresh } from "../../../utils/refreshContext"
import IncidentForm from "../incidentCreation/IncidentForm";
import ConfirmationNumberOutlinedIcon from '@mui/icons-material/ConfirmationNumberOutlined';

const Header = () => {
	//Custom Style menu
	const StyledMenu = styled((props) => (
		<Menu
			elevation={0}
			anchorOrigin={{
				vertical: 'bottom',
				horizontal: 'right',
			}}
			transformOrigin={{
				vertical: 'top',
				horizontal: 'right',
			}}
			{...props}
		/>
	))(({ theme }) => ({
		'& .MuiPaper-root': {
			borderRadius: 6,
			marginTop: theme.spacing(1),
			minWidth: 120,
			color: 'rgb(55, 65, 81)',
			boxShadow:
				'rgb(255, 255, 255) 0px 0px 0px 0px, rgba(0, 0, 0, 0.05) 0px 0px 0px 1px, rgba(0, 0, 0, 0.1) 0px 10px 15px -3px, rgba(0, 0, 0, 0.05) 0px 4px 6px -2px',
			'& .MuiMenu-list': {
				padding: '0px',
			},
			'& .MuiMenuItem-root': {
				fontSize: 12,
				'& .MuiListItemIcon-root': {
					minWidth: 17
				},
				'& .MuiSvgIcon-root': {
					fontSize: 12,
					color: theme.palette.text.secondary,
					marginRight: theme.spacing(0),
				},
				'&:active': {
					backgroundColor: alpha(
						theme.palette.primary.main,
						theme.palette.action.selectedOpacity,
					),
				},
			},
			...theme.applyStyles('dark', {
				color: theme.palette.grey[300],
			}),
		},
	}));

	const { accounts, instance } = useMsal();
	const activeAccount = accounts[0];
	const [firstName, setFirstName] = useState("");
	const navigate = useNavigate();
	let userInfo;
	try {
		userInfo = JSON.parse(localStorage.getItem('UserInfo'))?.user_info;
	} catch {
		userInfo = undefined;
	}
	const persona = userInfo?.persona?.toLowerCase() ?? "";
	const userType = userInfo?.userType?.toLowerCase() ?? "";

	// Incident Form Handlers
    const [openIncidentForm, setOpenIncidentForm] = useState(false);

	const handleLogout = () => {
		setAnchorEl(null);
		instance.logoutRedirect().catch((e) => {
			console.error(e);
		});
	};

	const getName = () => {
		const nameParts = activeAccount.name?.split(", ") || [];
		if (nameParts.length > 1) {
			setFirstName(nameParts[1] + ' ' + nameParts[0]);
		} else {
			setFirstName(activeAccount.name || "");
		}
	};

	useEffect(() => {
		if (accounts.length > 0) {
			getName();
		}
	}, [accounts, getName]);

	const getInitials = () => {
		const splitName = activeAccount.name.split(" ");
		return splitName
			.map((part) => part[0])
			.join("")
			.toUpperCase();
	};

	const [anchorEl, setAnchorEl] = useState(null);
	const open = Boolean(anchorEl);
	const handleClick = (event) => {
		setAnchorEl(event.currentTarget);
	};
	const handleClose = () => {
		setAnchorEl(null);
	};

	// for fetching the current week number
	function getWeekNumber(date = new Date()) {
		const tempDate = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
		const day = tempDate.getUTCDay() || 7;

		tempDate.setUTCDate(tempDate.getUTCDate() + 4 - day);

		const yearStart = new Date(Date.UTC(tempDate.getUTCFullYear(), 0, 1));
		const weekNo = Math.ceil((((tempDate - yearStart) / 86400000) + 1) / 7);

		return weekNo;
	}

	const { currentPageLabel, getRefreshDataByPage, currentSubPageLabel, formatRefreshTime } = useRefresh();

	// Get current page refresh data
	const getCurrentPageRefreshTime = () => {
		if (!currentPageLabel) return ' ';

		const pageData = getRefreshDataByPage(currentPageLabel, currentSubPageLabel);
		if (pageData.length === 0) return ' ';

		// Get the most recent refresh time for the current page
		const latestItem = [...pageData].sort(
			(a, b) => new Date(b.InsertedOn) - new Date(a.InsertedOn)
		)[0];

		return formatRefreshTime(latestItem?.InsertedOn);
	};


	return (
		<div className={styles.header}>
			<div className={styles.headerStart}>
				<img
					alt={headerConstants.headerHulLogo.name}
					src={headerConstants.headerHulLogo.path}
					className={styles.ulImg}
					onClick={() => navigate("/")}
				/>
				<div className={styles.headerTitle}>
					<span className={styles.headerSubtitle}>
						{headerConstants.headerTitle}
					</span>
					<div className={styles.dateCurrentWeek}>
						<span className={styles.headerDate}>
							Last refreshed - {" "}
							{getCurrentPageRefreshTime()}
						</span>
						<span className={styles.currentWeek}>
							Current Week: {getWeekNumber()}
						</span>
					</div>
				</div>
			</div >
			<div className={styles.headerEnd}>
				<div className={styles.headerIcons}>
					<IconContext.Provider
                        value={{ className: styles.headerIcon }}
                    >
                        <IoMdInformationCircleOutline onClick={() => setOpenIncidentForm(true)}/>
                    </IconContext.Provider>
					<IconContext.Provider
						value={{ className: styles.headerIcon }}
					>
						<IoIosSearch />
					</IconContext.Provider>

					<IconContext.Provider
						value={{ className: styles.headerIcon }}
					>
						<IoIosHelpCircleOutline />
					</IconContext.Provider>

					<IconContext.Provider
						value={{ className: styles.headerIcon }}
					>
						<IoIosNotificationsOutline />
					</IconContext.Provider>
				</div>
				<div className={styles.headerUserProfileSection}>
					<Avatar className={styles.avatar}>{getInitials()}</Avatar>
					<span className={styles.zeroMarginParagraph}>
						{firstName} <br />
						<em>{userInfo ? userInfo.persona : ""}, {userInfo ? userInfo.userType : ""}</em>
					</span>
				</div>
				<FaAngleDown size={10} onClick={handleClick} className={styles.headerLogoutButton} />
				<StyledMenu
					id="demo-customized-menu"
					MenuListProps={{
						'aria-labelledby': 'demo-customized-button',
					}}
					anchorEl={anchorEl}
					open={open}
					onClose={handleClose}
				>
					{(userType === 'admin' || userType === 'super admin') && <MenuItem onClick={() => {
						handleClose();
						navigate('/samarth/admin_portal');
					}}>
						<ListItemIcon>
							<User fontSize="small" />
						</ListItemIcon>
						User Management Portal
					</MenuItem>}
					 {(persona === "central team (coe)" || persona === "digital coe" || persona === "ey team") && 
					 <MenuItem onClick={() => {
						handleClose();
						navigate('/samarth/ticket_portal');
					}}>
						<ListItemIcon>
							<ConfirmationNumberOutlinedIcon fontSize="small" />
						</ListItemIcon>
						Changes Management Portal
					</MenuItem>}
					<MenuItem onClick={handleLogout}>
						<ListItemIcon>
							<Logout fontSize="small" />
						</ListItemIcon>
						Logout
					</MenuItem>
				</StyledMenu>
			</div>
			<IncidentForm open={openIncidentForm} onClose={() => setOpenIncidentForm(false)} firstName={firstName} />
		</div >
	);
};

export default Header;
